import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../lib/redux/hooks';
import { Calendar } from '../../../../ui/molecules/calendar';
import { MadfarmInput } from '../../../../ui/molecules/input/newInput';
import MultiSelectCheckbox from '../../../../ui/molecules/multiSelectCheckbox';
import { ProtocolSearch } from '../../../../ui/molecules/ProtocolSearch';
import RadioButtonGroup from '../../../../ui/molecules/RadioButtonGroup';
import TextArea from '../../../../ui/molecules/textArea';
import { OptionType, WebinarOptions } from '../../../../utils';
import { GetByIdAnagraphic, resetGetByIdStatus } from '../../boxAnagrafica/slice';
import { GetAllPersonalManagement, resetGetAllPersonalManagementResourcesStatus } from '../../slice';
import { addPMResource, resetPMResources, setTrainingAttendanceRegister, setTrainingCredits, setTrainingEndingDate, setTrainingHeadquarter, setTrainingOtherData, setTrainingParticipants, setTrainingStartingDate, setTrainingTopicsCovered, setTrainingWebinar } from '../slice';
import { ActivityAdd } from './components/activity';
import { ExternalTeachersAdd } from './components/extTeachers';
import { InternalTeachersAdd } from './components/intTeachers';

export function TrainingPopupAdd() {
    const dispatch = useAppDispatch()
    const trainingState = useAppSelector(state => state.PMTraining)
    const PMState = useAppSelector(state => state.personalManagement)
    const anagraficaState = useAppSelector(state => state.PMAnagrafica)
    const [selectedOption, setSelectedOption] = useState<WebinarOptions | null>(WebinarOptions.WEBINAR)
    const [participants, setParticipants] = useState<OptionType[]>([])

    useEffect(() => {
        dispatch(resetPMResources())
        dispatch(GetAllPersonalManagement(PMState.filters))
    }, [])

    useEffect(() => {
        if (
            PMState.getAllPersonalManagementResourcesStatus === 'successfully' &&
            PMState.getAllPersonalManagementResourcesResponse !== undefined
        ) {
            dispatch(resetGetAllPersonalManagementResourcesStatus())
            const data = PMState.getAllPersonalManagementResourcesResponse.data
            for (let i = 0; i < data.length; i++) {
                dispatch(GetByIdAnagraphic(String(data[i].anagraphicId)))
            }
        }
    }, [PMState.getAllPersonalManagementResourcesStatus])

    useEffect(() => {
        if (
            anagraficaState.GetByIdAnagraphicStatus === 'successfully' &&
            anagraficaState.getByIdAnagraphicRequest !== undefined
        ) {
            dispatch(resetGetByIdStatus('idle'))
            const data = anagraficaState.getByIdAnagraphicRequest
            dispatch(addPMResource({ value: data.personalManagementId, label: `${data.name} ${data.surname}` }))
        }
    }, [anagraficaState.GetByIdAnagraphicStatus])

    const handleOptionChange = (value: WebinarOptions) => {
        setSelectedOption(value)
        dispatch(setTrainingWebinar(value === 'WEBINAR'))
    }

    return (
        <div className="p-6 bg-white flex flex-col gap-6">
            <div className="flex flex-col gap-4 pb-6 border-b border-neutral-200">
                <div className="flex items-center gap-6">
                    <div className="shrink-0">
                        <RadioButtonGroup
                            name="webinar"
                            options={Object.values(WebinarOptions)}
                            selectedValue={selectedOption}
                            onChange={handleOptionChange}
                            optionLabels={{
                                [WebinarOptions.WEBINAR]: 'Webinar',
                                [WebinarOptions.SEDE]: 'In Presenza',
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <MadfarmInput
                            label="Specifica sede"
                            placeholder="Specifica sede"
                            disabled={selectedOption === WebinarOptions.WEBINAR}
                            value={trainingState.trainingCreationRequest.sede}
                            defaultValue={trainingState.trainingCreationRequest.sede}
                            onChangeText={(text) => dispatch(setTrainingHeadquarter(text))}
                            error={trainingState.trainingErrors.sede}
                            supportingText={trainingState.trainingErrors.sede ? 'Specifica sede' : undefined}
                        />
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-6 border-b border-neutral-200">
                <Calendar
                    label="Dal"
                    selected={trainingState.trainingCreationRequest.starting}
                    onChange={(date) => dispatch(setTrainingStartingDate(format(new Date(date), 'yyyy-MM-dd')))}
                />
                <Calendar
                    label="Al"
                    selected={trainingState.trainingCreationRequest.ending}
                    onChange={(date) => dispatch(setTrainingEndingDate(format(new Date(date), 'yyyy-MM-dd')))}
                />
                <MadfarmInput
                    label="Crediti"
                    placeholder="Specificare numero crediti"
                    value={trainingState.trainingCreationRequest.credits}
                    defaultValue={trainingState.trainingCreationRequest.credits}
                    onChangeText={(text) => dispatch(setTrainingCredits(text))}
                    error={trainingState.trainingErrors.credits}
                    supportingText={trainingState.trainingErrors.credits ? 'Crediti mancanti' : undefined}
                />
            </div>
            {trainingState.trainingErrors.activities && (
                <div className="rounded-lg bg-red-100 px-4 py-3">
                    <span className="text-sm font-semibold text-red-700">
                        Attività o campi dell'attività mancanti!
                    </span>
                </div>
            )}
            <ActivityAdd />
            {trainingState.trainingErrors.teacher && (
                <div className="rounded-lg bg-red-100 px-4 py-3">
                    <span className="text-sm font-semibold text-red-700">
                        Deve essere presente almeno un insegnante, che sia interno od esterno.
                    </span>
                </div>
            )}
            <InternalTeachersAdd />
            <ExternalTeachersAdd />
            <div className="pb-6 border-b border-neutral-200">
                <MultiSelectCheckbox
                    label="Partecipanti"
                    error={trainingState.trainingErrors.participants}
                    errorLabel="Non ci sono partecipanti!"
                    onChange={(e) => {
                        setParticipants(e.value)
                        dispatch(setTrainingParticipants(e.value))
                    }}
                    value={participants}
                    option={trainingState.PMResources}
                    placeholder="Partecipanti"
                    selectAllLabel="Tutti"
                />
            </div>
            <div className="border-t border-neutral-200 pt-2">
                <p className="text-md font-semibold uppercase tracking-widest text-brandPrimary-600">
                    Registro Presenze — CDX RPA
                </p>
            </div>
            <div className="pb-6 border-b border-neutral-200">
                <ProtocolSearch
                    type="entry"
                    errorLabel={trainingState.trainingErrors.attendanceRegister ? 'Protocollo mancante' : undefined}
                    onSelect={(value) => dispatch(setTrainingAttendanceRegister(value))}
                />
            </div>
            <div className="pb-6 border-b border-neutral-200">
                <TextArea
                    label="Argomenti trattati"
                    placeholder="Descrivi in dettaglio gli argomenti trattati"
                    error={trainingState.trainingErrors.topicsCovered}
                    supportingText={trainingState.trainingErrors.topicsCovered ? 'Non è stato inserito alcun argomento' : undefined}
                    onChangeText={(value) => dispatch(setTrainingTopicsCovered(value))}
                />
            </div>
            <div>
                <TextArea
                    label="Altri dati"
                    onChangeText={(value) => dispatch(setTrainingOtherData(value))}
                />
            </div>
        </div>
    )
}